import {
  Avatar,
  Box,
  Button,
  Grid,
  MenuItem,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useMemo, useState } from 'react';

import { PageHeader } from '@opengov/components-page-header';
import {
  CalendarBlank,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  ClockOutline,
  Magnify,
} from '@opengov/react-capital-assets';

import { MetricCard } from '../components/shared/MetricCard';
import { CaseStatusBadge } from '../components/vcm/CaseStatusBadge';
import { SectionCard } from '../components/vcm/SectionCard';
import { TierBadge } from '../components/vcm/TierBadge';
import { useVcm } from '../contexts/VcmContext';
import { useAppNavigation } from '../hooks/useAppNavigation';
import { getInitials } from '../lib/vcm/computed';
/* MOCK DATA — replace with real /v2 API calls when backend is connected */
import { mockGetAllFollowUps, mockGetAllReferrals, mockGetCases } from '../mocks/vcm-mock-data';
import type { FollowUp, TcrCase, TcrReferral } from '../types/vcm';

/* ── Local types ─────────────────────────────────────────────────────── */

type MetricFilter = 'all' | 'overdue' | 'housing' | 'referrals';
type UrgencyLevel = 'critical' | 'warning' | 'ok';
type TabFilter = 'needs-action' | 'on-track' | 'all';
type QuickField = 'currentResidence' | 'progression' | 'vaClaimStatus';

interface Urgency {
  readonly score: number;
  readonly level: UrgencyLevel;
}

interface QuickFilterState {
  readonly field: QuickField;
  readonly value: string;
}

interface ActionItem {
  readonly id: string;
  readonly caseId: string;
  readonly client: string;
  readonly reason: string;
  readonly dueLabel: string;
  readonly tab: 'notes' | 'housing' | 'referrals';
  readonly level: UrgencyLevel;
}

interface Bucket {
  readonly value: string;
  readonly count: number;
}

interface WeekDay {
  readonly key: string;
  readonly weekday: string;
  readonly dayOfMonth: number;
  readonly count: number;
  readonly isToday: boolean;
}

/* ── Date helpers (pure) ─────────────────────────────────────────────── */

const MS_PER_DAY = 86_400_000;
const TIER_MULTIPLIER: Record<string, number> = { 'Tier 4': 2, 'Tier 3': 1.5, 'Tier 2': 1, 'Tier 1': 0.75 };
const NOT_SET = 'Not set';

function atMidnight(date: Date): Date {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
}

function parseDate(value: string | null): Date | null {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Whole days from `today` (midnight) to `value`. Negative = in the past. */
function daysFromToday(value: string | null, today: Date): number | null {
  const parsed = parseDate(value);
  if (!parsed) return null;
  return Math.round((atMidnight(parsed).getTime() - today.getTime()) / MS_PER_DAY);
}

function dateKey(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

function startOfWeek(today: Date): Date {
  const monday = new Date(today);
  const offset = (monday.getDay() + 6) % 7;
  monday.setDate(monday.getDate() - offset);
  return atMidnight(monday);
}

function isOverdueFollowUp(followUp: FollowUp | undefined, today: Date): boolean {
  if (!followUp) return false;
  const diff = daysFromToday(followUp.nextFollowUpDate, today);
  return diff !== null && diff < 0;
}

function isHousingExpiring(tcrCase: TcrCase, today: Date): boolean {
  const diff = daysFromToday(tcrCase.temporaryHousingEndDate, today);
  return diff !== null && diff <= 30;
}

function isPendingReferral(referral: TcrReferral): boolean {
  return referral.status === 'Requested' || referral.status === 'Referred';
}

function relativeDueLabel(value: string | null, today: Date): string {
  const diff = daysFromToday(value, today);
  if (diff === null) return 'No date set';
  if (diff < 0) return `${Math.abs(diff)} day${Math.abs(diff) === 1 ? '' : 's'} overdue`;
  if (diff === 0) return 'Due today';
  if (diff === 1) return 'Due tomorrow';
  return `Due in ${diff} days`;
}

function housingDueLabel(value: string | null, today: Date): string {
  const diff = daysFromToday(value, today);
  if (diff === null) return 'No end date';
  if (diff < 0) return `Ended ${Math.abs(diff)} day${Math.abs(diff) === 1 ? '' : 's'} ago`;
  if (diff === 0) return 'Ends today';
  return `Ends in ${diff} days`;
}

/** Days since the last update (case contact). Returns 0 when unknown. */
function daysSinceContact(tcrCase: TcrCase, today: Date): number {
  const diff = daysFromToday(tcrCase.updatedAt, today);
  return diff === null ? 0 : Math.max(0, -diff);
}

/**
 * Local urgency score + level for a case. Tier sets the baseline; overdue
 * follow-ups and expiring housing escalate to critical, pending referrals or
 * stale contact escalate to warning.
 */
function computeUrgency(
  tcrCase: TcrCase,
  followUp: FollowUp | undefined,
  referrals: readonly TcrReferral[],
  today: Date,
): Urgency {
  const overdue = isOverdueFollowUp(followUp, today);
  const housingExpiring = isHousingExpiring(tcrCase, today);
  const pendingCount = referrals.filter(isPendingReferral).length;
  const stale = daysSinceContact(tcrCase, today);

  let score = 0;

  // Overdue follow-up: +100 per day overdue
  if (overdue && followUp?.nextFollowUpDate) {
    const daysOverdue = Math.ceil((today.getTime() - new Date(`${followUp.nextFollowUpDate}T00:00:00`).getTime()) / 86_400_000);
    score += 100 * daysOverdue;
  }

  // Housing expiring within 30 days: +50 / days remaining
  if (housingExpiring && tcrCase.temporaryHousingEndDate) {
    const daysLeft = Math.ceil((new Date(`${tcrCase.temporaryHousingEndDate}T00:00:00`).getTime() - today.getTime()) / 86_400_000);
    score += Math.round(50 / Math.max(daysLeft, 1));
  }

  // Pending referrals: +20 each
  score += pendingCount * 20;

  // Days since last contact: +1 per day over 7
  if (stale > 7) score += stale - 7;

  // Tier multiplier
  score = Math.round(score * (TIER_MULTIPLIER[tcrCase.tier] ?? 1));

  let level: UrgencyLevel = 'ok';
  if (overdue || housingExpiring) level = 'critical';
  else if (pendingCount > 0 || stale > 7) level = 'warning';

  return { score, level };
}

function buildBuckets(cases: readonly TcrCase[], field: QuickField): Bucket[] {
  const counts = new Map<string, number>();
  for (const tcrCase of cases) {
    const raw = tcrCase[field];
    const value = raw == null || raw === '' ? NOT_SET : String(raw);
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([value, count]) => ({ value, count }))
    .sort((a, b) => b.count - a.count);
}

const LEVEL_RANK: Record<UrgencyLevel, number> = { critical: 0, warning: 1, ok: 2 };
const STATUS_OPTIONS = ['All', 'Open', 'Review', 'Pending', 'Closed'] as const;
const TIER_OPTIONS = ['All', 'Tier 1', 'Tier 2', 'Tier 3', 'Tier 4'] as const;
const ALL_EVENTS = 'All events';
const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;

export function ManageMyCasesPage() {
  const { navigateToPath } = useAppNavigation();
  const { currentUser, currentEvent } = useVcm();

  /* MOCK DATA — static seed; scoped to the signed-in case manager. */
  const today = useMemo(() => atMidnight(new Date()), []);
  const allCases = useMemo(() => mockGetCases(), []);
  const allFollowUps = useMemo(() => mockGetAllFollowUps(), []);
  const allReferrals = useMemo(() => mockGetAllReferrals(), []);

  const myCases = useMemo(
    () => allCases.filter((c) => c.assignedManager === currentUser.name),
    [allCases, currentUser.name],
  );

  const myEvents = useMemo(
    () => [...new Set(myCases.map((c) => c.program ?? '(unknown)'))].sort((a, b) => a.localeCompare(b)),
    [myCases],
  );

  const defaultEvent = myEvents.includes(currentEvent.name) ? currentEvent.name : ALL_EVENTS;

  /* ── Filter state ── */
  const [eventFilter, setEventFilter] = useState<string>(defaultEvent);
  const [metricFilter, setMetricFilter] = useState<MetricFilter>('all');
  const [quickFilter, setQuickFilter] = useState<QuickFilterState | null>(null);
  const [weekDayFilter, setWeekDayFilter] = useState<string | null>(null);
  const [tabFilter, setTabFilter] = useState<TabFilter>('all');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('All');
  const [tierFilter, setTierFilter] = useState<string>('All');
  const [showAllPriority, setShowAllPriority] = useState(false);

  /* Cases scoped to the selected event — the base set for everything below. */
  const scopedCases = useMemo(
    () => (eventFilter === ALL_EVENTS ? myCases : myCases.filter((c) => ( c.program ?? '' ) === eventFilter)),
    [myCases, eventFilter],
  );

  const scopedCaseIds = useMemo(() => new Set(scopedCases.map((c) => c.caseId)), [scopedCases]);

  const caseMap = useMemo(() => {
    const map = new Map<string, TcrCase>();
    for (const c of scopedCases) map.set(c.caseId, c);
    return map;
  }, [scopedCases]);

  const followUpMap = useMemo(() => {
    const map = new Map<string, FollowUp>();
    for (const followUp of allFollowUps) {
      if (scopedCaseIds.has(followUp.caseId) && !map.has(followUp.caseId)) {
        map.set(followUp.caseId, followUp);
      }
    }
    return map;
  }, [allFollowUps, scopedCaseIds]);

  const referralMap = useMemo(() => {
    const map = new Map<string, TcrReferral[]>();
    for (const referral of allReferrals) {
      if (!scopedCaseIds.has(referral.caseId)) continue;
      const list = map.get(referral.caseId) ?? [];
      list.push(referral);
      map.set(referral.caseId, list);
    }
    return map;
  }, [allReferrals, scopedCaseIds]);

  const urgencyMap = useMemo(() => {
    const map = new Map<string, Urgency>();
    for (const c of scopedCases) {
      map.set(
        c.caseId,
        computeUrgency(c, followUpMap.get(c.caseId), referralMap.get(c.caseId) ?? [], today),
      );
    }
    return map;
  }, [scopedCases, followUpMap, referralMap, today]);

  /* ── Metric counts ── */
  const openCount = useMemo(
    () => scopedCases.filter((c) => c.status !== 'Closed').length,
    [scopedCases],
  );
  const overdueFollowUps = useMemo(
    () => [...followUpMap.values()].filter((fu) => isOverdueFollowUp(fu, today)),
    [followUpMap, today],
  );
  const expiringHousingCases = useMemo(
    () => scopedCases.filter((c) => isHousingExpiring(c, today)),
    [scopedCases, today],
  );
  const pendingReferrals = useMemo(
    () => [...referralMap.values()].flat().filter(isPendingReferral),
    [referralMap],
  );

  /* ── Today's priority items ── */
  const actionItems = useMemo<ActionItem[]>(() => {
    const items: ActionItem[] = [];
    for (const followUp of overdueFollowUps) {
      const client = caseMap.get(followUp.caseId)?.clientName ?? followUp.caseId;
      items.push({
        id: `fu-${followUp.id}`,
        caseId: followUp.caseId,
        client,
        reason: `Overdue ${followUp.followUpType?.toLowerCase() ?? ''} follow-up`.trim(),
        dueLabel: relativeDueLabel(followUp.nextFollowUpDate, today),
        tab: 'notes',
        level: 'critical',
      });
    }
    for (const c of expiringHousingCases) {
      items.push({
        id: `housing-${c.caseId}`,
        caseId: c.caseId,
        client: c.clientName,
        reason: 'Temporary housing ending',
        dueLabel: housingDueLabel(c.temporaryHousingEndDate, today),
        tab: 'housing',
        level: 'critical',
      });
    }
    for (const referral of pendingReferrals) {
      const client = caseMap.get(referral.caseId)?.clientName ?? referral.caseId;
      items.push({
        id: `ref-${referral.id}`,
        caseId: referral.caseId,
        client,
        reason: `Referral ${referral.status.toLowerCase()}: ${referral.serviceName}`,
        dueLabel: referral.agencyName,
        tab: 'referrals',
        level: 'warning',
      });
    }
    return items.sort((a, b) => LEVEL_RANK[a.level] - LEVEL_RANK[b.level]);
  }, [overdueFollowUps, expiringHousingCases, pendingReferrals, caseMap, today]);

  const visiblePriority = showAllPriority ? actionItems : actionItems.slice(0, 5);

  /* ── Quick-filter buckets ── */
  const residenceBuckets = useMemo(
    () => buildBuckets(scopedCases, 'currentResidence'),
    [scopedCases],
  );
  const progressionBuckets = useMemo(() => buildBuckets(scopedCases, 'progression'), [scopedCases]);
  const femaBuckets = useMemo(
    () => buildBuckets(scopedCases, 'vaClaimStatus'),
    [scopedCases],
  );

  /* ── Due-this-week strip ── */
  const weekDays = useMemo<WeekDay[]>(() => {
    const monday = startOfWeek(today);
    const todayKey = dateKey(today);
    const dueByDay = new Map<string, number>();
    for (const followUp of followUpMap.values()) {
      const due = parseDate(followUp.nextFollowUpDate);
      if (!due) continue;
      const key = dateKey(atMidnight(due));
      dueByDay.set(key, (dueByDay.get(key) ?? 0) + 1);
    }
    return WEEKDAY_LABELS.map((weekday, index) => {
      const day = new Date(monday);
      day.setDate(monday.getDate() + index);
      const key = dateKey(day);
      return {
        key,
        weekday,
        dayOfMonth: day.getDate(),
        count: dueByDay.get(key) ?? 0,
        isToday: key === todayKey,
      };
    });
  }, [followUpMap, today]);

  /* ── Table filtering pipeline ── */
  const filteredCases = useMemo(() => {
    const query = search.toLowerCase().trim();
    const overdueIds = new Set(overdueFollowUps.map((fu) => fu.caseId));
    const housingIds = new Set(expiringHousingCases.map((c) => c.caseId));
    const referralIds = new Set(pendingReferrals.map((r) => r.caseId));

    const matchesMetric = (c: TcrCase): boolean => {
      switch (metricFilter) {
        case 'overdue':
          return overdueIds.has(c.caseId);
        case 'housing':
          return housingIds.has(c.caseId);
        case 'referrals':
          return referralIds.has(c.caseId);
        default:
          return true;
      }
    };

    const matchesQuick = (c: TcrCase): boolean => {
      if (!quickFilter) return true;
      const raw = c[quickFilter.field];
      const value = raw == null || raw === '' ? NOT_SET : String(raw);
      return value === quickFilter.value;
    };

    const matchesWeekDay = (c: TcrCase): boolean => {
      if (!weekDayFilter) return true;
      const followUp = followUpMap.get(c.caseId);
      const due = parseDate(followUp?.nextFollowUpDate ?? null);
      return due != null && dateKey(atMidnight(due)) === weekDayFilter;
    };

    const matchesTab = (c: TcrCase): boolean => {
      const level = urgencyMap.get(c.caseId)?.level ?? 'ok';
      if (tabFilter === 'needs-action') return level !== 'ok';
      if (tabFilter === 'on-track') return level === 'ok';
      return true;
    };

    const matchesSearch = (c: TcrCase): boolean => {
      if (!query) return true;
      return `${c.clientName} ${c.caseId} ${c.county} ${c.event}`.toLowerCase().includes(query);
    };

    const matchesStatus = (c: TcrCase): boolean => statusFilter === 'All' || c.status === statusFilter;
    const matchesTier = (c: TcrCase): boolean => tierFilter === 'All' || c.tier === tierFilter;

    return scopedCases
      .filter(
        (c) =>
          matchesMetric(c) &&
          matchesQuick(c) &&
          matchesWeekDay(c) &&
          matchesTab(c) &&
          matchesSearch(c) &&
          matchesStatus(c) &&
          matchesTier(c),
      )
      .sort((a, b) => (urgencyMap.get(b.caseId)?.score ?? 0) - (urgencyMap.get(a.caseId)?.score ?? 0));
  }, [
    scopedCases,
    metricFilter,
    quickFilter,
    weekDayFilter,
    tabFilter,
    search,
    statusFilter,
    tierFilter,
    overdueFollowUps,
    expiringHousingCases,
    pendingReferrals,
    followUpMap,
    urgencyMap,
  ]);

  const tabCounts = useMemo(() => {
    let needsAction = 0;
    let onTrack = 0;
    for (const c of scopedCases) {
      if ((urgencyMap.get(c.caseId)?.level ?? 'ok') === 'ok') onTrack += 1;
      else needsAction += 1;
    }
    return { needsAction, onTrack, all: scopedCases.length };
  }, [scopedCases, urgencyMap]);

  const anyFilterActive =
    metricFilter !== 'all' ||
    quickFilter !== null ||
    weekDayFilter !== null ||
    tabFilter !== 'all' ||
    search.trim() !== '' ||
    statusFilter !== 'All' ||
    tierFilter !== 'All';

  const resetAll = () => {
    setMetricFilter('all');
    setQuickFilter(null);
    setWeekDayFilter(null);
    setTabFilter('all');
    setSearch('');
    setStatusFilter('All');
    setTierFilter('All');
  };

  const toggleMetric = (next: MetricFilter) => {
    setMetricFilter((current) => (current === next ? 'all' : next));
  };

  const toggleQuick = (field: QuickField, value: string) => {
    setQuickFilter((current) =>
      current && current.field === field && current.value === value ? null : { field, value },
    );
  };

  const isQuickActive = (field: QuickField, value: string): boolean =>
    quickFilter?.field === field && quickFilter.value === value;

  return (
    <Box
      sx={{
        p: (theme) => theme.spacing(3),
        display: 'flex',
        flexDirection: 'column',
        gap: (theme) => theme.spacing(2.5),
      }}
    >
      <PageHeader
        title="My Cases"
        description="Cases assigned to you."
        actions={[
          <TextField
            key="event-filter"
            select
            size="small"
            value={eventFilter}
            onChange={(e) => setEventFilter(e.target.value)}
            sx={{
              minWidth: 240,
              '& .MuiInputBase-root': { height: 32, fontSize: 13, fontWeight: 600 },
              '& .MuiOutlinedInput-notchedOutline': { borderColor: (t) => t.palette.divider },
            }}
          >
            <MenuItem value={ALL_EVENTS} sx={{ fontSize: 13 }}>All Events</MenuItem>
            {myEvents.map((event) => (
              <MenuItem key={event} value={event} sx={{ fontSize: 13 }}>
                {event}
              </MenuItem>
            ))}
          </TextField>,
        ]}
      />

      {/* ── Metric cards (toggle filters) ── */}
      {/* MOCK DATA — metrics derived from scoped seed cases */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricTile
            active={metricFilter === 'all'}
            title="Open Cases"
            value={String(openCount)}
            subtitle="Active case files"
            onClick={() => toggleMetric('all')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricTile
            active={metricFilter === 'overdue'}
            title="Overdue Follow-ups"
            value={String(overdueFollowUps.length)}
            subtitle="Past their due date"
            onClick={() => toggleMetric('overdue')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricTile
            active={metricFilter === 'housing'}
            title="Expiring Housing"
            value={String(expiringHousingCases.length)}
            subtitle="Ending within 30 days"
            onClick={() => toggleMetric('housing')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricTile
            active={metricFilter === 'referrals'}
            title="Pending Referrals"
            value={String(pendingReferrals.length)}
            subtitle="Awaiting an outcome"
            onClick={() => toggleMetric('referrals')}
          />
        </Grid>
      </Grid>

      {/* ── Today's priority ── */}
      {/* MOCK DATA — priority items from overdue follow-ups, housing, referrals */}
      <SectionCard
        title="Today's Priority"
        action={
          actionItems.length > 5 ? (
            <Button
              size="small"
              variant="text"
              endIcon={showAllPriority ? <ChevronUp /> : <ChevronDown />}
              onClick={() => setShowAllPriority((prev) => !prev)}
            >
              {showAllPriority ? 'Show less' : `Show all (${actionItems.length})`}
            </Button>
          ) : undefined
        }
        disableContentPadding
      >
        {actionItems.length === 0 ? (
          <Stack
            alignItems="center"
            spacing={1}
            sx={{ py: (theme) => theme.spacing(5), color: (theme) => theme.palette.text.secondary }}
          >
            <Box
              sx={{
                display: 'flex',
                color: (theme) => theme.palette.success.main,
                '& svg': { width: 32, height: 32 },
              }}
            >
              <CheckCircle />
            </Box>
            <Typography variant="subtitle2">All caught up</Typography>
            <Typography variant="caption">No overdue follow-ups, housing, or referrals.</Typography>
          </Stack>
        ) : (
          <Stack divider={<Box sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}` }} />}>
            {visiblePriority.map((item) => (
              <Stack
                key={item.id}
                component="button"
                direction="row"
                alignItems="center"
                spacing={2}
                onClick={() => navigateToPath(`cases/${item.caseId}?tab=${item.tab}`)}
                sx={{
                  textAlign: 'left',
                  width: '100%',
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  px: (theme) => theme.spacing(2),
                  py: (theme) => theme.spacing(1.5),
                  '&:hover': { backgroundColor: (theme) => theme.palette.action.hover },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    color: (theme) =>
                      item.level === 'critical'
                        ? theme.palette.error.main
                        : theme.palette.warning.main,
                    '& svg': { width: 20, height: 20 },
                  }}
                >
                  {item.tab === 'housing' ? <CalendarBlank /> : <ClockOutline />}
                </Box>
                <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {item.client}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: (theme) => theme.palette.text.secondary }}
                  >
                    {item.reason}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    color: (theme) =>
                      item.level === 'critical'
                        ? theme.palette.error.main
                        : theme.palette.text.secondary,
                  }}
                >
                  {item.dueLabel}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
      </SectionCard>

      {/* ── Quick filters ── */}
      {/* MOCK DATA — bucketed counts across scoped cases */}
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 4 }}>
          <QuickFilterCard
            title="Housing Situation"
            field="currentResidence"
            buckets={residenceBuckets}
            isActive={isQuickActive}
            onToggle={toggleQuick}
            onClear={() => setQuickFilter(null)}
            showClear={quickFilter?.field === 'currentResidence'}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <QuickFilterCard
            title="Case Progress"
            field="progression"
            buckets={progressionBuckets}
            isActive={isQuickActive}
            onToggle={toggleQuick}
            onClear={() => setQuickFilter(null)}
            showClear={quickFilter?.field === 'progression'}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <QuickFilterCard
            title="VA Claim Status"
            field="vaClaimStatus"
            buckets={femaBuckets}
            isActive={isQuickActive}
            onToggle={toggleQuick}
            onClear={() => setQuickFilter(null)}
            showClear={quickFilter?.field === 'vaClaimStatus'}
          />
        </Grid>
      </Grid>

      {/* ── Due this week ── */}
      {/* MOCK DATA — follow-up counts per weekday */}
      <SectionCard title="Due this week">
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
          {weekDays.map((day) => {
            const selected = weekDayFilter === day.key;
            return (
              <Button
                key={day.key}
                variant={selected ? 'contained' : 'outlined'}
                onClick={() =>
                  setWeekDayFilter((current) => (current === day.key ? null : day.key))
                }
                sx={{
                  flex: '1 1 0',
                  minWidth: 0,
                  flexDirection: 'column',
                  borderColor: (theme) =>
                    day.isToday && !selected ? theme.palette.primary.main : undefined,
                }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {day.weekday} {day.dayOfMonth}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {day.count}
                </Typography>
              </Button>
            );
          })}
        </Stack>
      </SectionCard>

      {/* ── Cases table ── */}
      {/* MOCK DATA — scoped + filtered cases */}
      <SectionCard title="Cases" disableContentPadding>
        <Tabs
          value={tabFilter}
          onChange={(_e, value: TabFilter) => setTabFilter(value)}
          sx={{ px: (theme) => theme.spacing(2), borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}
        >
          <Tab value="needs-action" label={`Needs action (${tabCounts.needsAction})`} />
          <Tab value="on-track" label={`On track (${tabCounts.onTrack})`} />
          <Tab value="all" label={`All (${tabCounts.all})`} />
        </Tabs>

        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'stretch', md: 'center' }}
          spacing={1.5}
          sx={{ p: (theme) => theme.spacing(2) }}
        >
          {anyFilterActive && (
            <Button size="small" variant="outlined" onClick={resetAll}>
              Reset all
            </Button>
          )}
          <Typography
            variant="caption"
            sx={{ fontWeight: 600, color: (theme) => theme.palette.text.secondary, whiteSpace: 'nowrap' }}
          >
            {filteredCases.length} of {scopedCases.length}
          </Typography>
          <TextField
            placeholder="Search client, case ID, county, event…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ flex: 1, minWidth: (theme) => theme.spacing(20) }}
            slotProps={{
              input: {
                startAdornment: (
                  <Box
                    sx={{
                      display: 'flex',
                      mr: 1,
                      color: (theme) => theme.palette.text.secondary,
                      '& svg': { width: 18, height: 18 },
                    }}
                  >
                    <Magnify />
                  </Box>
                ),
              },
            }}
          />
          <TextField
            select
            size="small"
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: (theme) => theme.spacing(16) }}
          >
            {STATUS_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Tier"
            value={tierFilter}
            onChange={(e) => setTierFilter(e.target.value)}
            sx={{ minWidth: (theme) => theme.spacing(16) }}
          >
            {TIER_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        <TableContainer sx={{ maxHeight: 480 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 32 }} />
                <TableCell align="center">Client</TableCell>
                <TableCell align="center" sx={{ width: 80 }}>Tier</TableCell>
                <TableCell align="center" sx={{ width: 90 }}>Status</TableCell>
                <TableCell align="center" sx={{ width: 100 }}>Progression</TableCell>
                <TableCell align="center" sx={{ width: 100 }}>County</TableCell>
                <TableCell align="right" sx={{ width: 140 }}>Days since contact</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCases.map((tcrCase) => {
                const level = urgencyMap.get(tcrCase.caseId)?.level ?? 'ok';
                const stale = daysSinceContact(tcrCase, today);
                return (
                  <TableRow
                    key={tcrCase.caseId}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigateToPath(`cases/${tcrCase.caseId}`)}
                  >
                    <TableCell sx={{ width: (theme) => theme.spacing(4) }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          backgroundColor: (theme) =>
                            level === 'critical'
                              ? theme.palette.error.main
                              : level === 'warning'
                                ? theme.palette.warning.main
                                : theme.palette.success.main,
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            fontSize: (theme) => theme.typography.caption.fontSize,
                            fontWeight: 700,
                            bgcolor: (theme) => theme.palette.primary.main,
                          }}
                        >
                          {getInitials(tcrCase.clientName)}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {tcrCase.clientName}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: (theme) => theme.palette.text.secondary }}
                          >
                            {tcrCase.caseId}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <TierBadge tier={tcrCase.tier} />
                    </TableCell>
                    <TableCell align="center">
                      <CaseStatusBadge status={tcrCase.status} />
                    </TableCell>
                    <TableCell align="center">{tcrCase.progression}</TableCell>
                    <TableCell align="center">{tcrCase.county}</TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: stale > 7 ? 700 : 400,
                          color: (theme) =>
                            stale > 14
                              ? theme.palette.error.main
                              : stale > 7
                                ? theme.palette.warning.main
                                : theme.palette.text.primary,
                        }}
                      >
                        {stale}d
                      </Typography>
                    </TableCell>
                  </TableRow>
                );
              })}
              {filteredCases.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: (theme) => theme.spacing(4) }}>
                    <Typography
                      variant="body2"
                      sx={{ color: (theme) => theme.palette.text.secondary }}
                    >
                      No cases match the current filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </SectionCard>
    </Box>
  );
}

/* ── Local components ────────────────────────────────────────────────── */

interface MetricTileProps {
  readonly active: boolean;
  readonly title: string;
  readonly value: string;
  readonly subtitle: string;
  readonly onClick: () => void;
}

function MetricTile({ active, title, value, subtitle, onClick }: MetricTileProps) {
  return (
    <Box
      sx={{
        height: '100%',
        borderRadius: 1,
        transition: (theme) => theme.transitions.create('box-shadow'),
        boxShadow: (theme) => (active ? `0 0 0 2px ${theme.palette.primary.main}` : 'none'),
      }}
    >
      <MetricCard center title={title} value={value} subtitle={subtitle} onClick={onClick} />
    </Box>
  );
}

interface QuickFilterCardProps {
  readonly title: string;
  readonly field: QuickField;
  readonly buckets: readonly Bucket[];
  readonly isActive: (field: QuickField, value: string) => boolean;
  readonly onToggle: (field: QuickField, value: string) => void;
  readonly onClear: () => void;
  readonly showClear: boolean;
}

function QuickFilterCard({
  title,
  field,
  buckets,
  isActive,
  onToggle,
  onClear,
  showClear,
}: QuickFilterCardProps) {
  return (
    <SectionCard
      title={title}
      action={
        showClear ? (
          <Button size="small" variant="text" onClick={onClear}>
            Clear
          </Button>
        ) : undefined
      }
      disableContentPadding
    >
      <Stack>
        {buckets.length === 0 ? (
          <Typography
            variant="caption"
            sx={{
              p: (theme) => theme.spacing(2),
              color: (theme) => theme.palette.text.secondary,
            }}
          >
            No cases.
          </Typography>
        ) : (
          buckets.map((bucket) => {
            const active = isActive(field, bucket.value);
            return (
              <Stack
                key={bucket.value}
                component="button"
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                onClick={() => onToggle(field, bucket.value)}
                sx={{
                  width: '100%',
                  border: 'none',
                  cursor: 'pointer',
                  px: (theme) => theme.spacing(2),
                  py: (theme) => theme.spacing(1),
                  backgroundColor: (theme) =>
                    active ? theme.palette.action.selected : 'transparent',
                  '&:hover': { backgroundColor: (theme) => theme.palette.action.hover },
                }}
              >
                <Typography
                  variant="body2"
                  noWrap
                  sx={{ fontWeight: active ? 700 : 400, textAlign: 'left', flex: 1, minWidth: 0 }}
                >
                  {bucket.value}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 700, color: (theme) => theme.palette.text.secondary }}
                >
                  {bucket.count}
                </Typography>
              </Stack>
            );
          })
        )}
      </Stack>
    </SectionCard>
  );
}
